import React, { useState, useEffect } from 'react';
import { navLinks, companyInfo } from '../data/mock';
import { Menu, X, Sun, Moon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const NexmoveHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? isDark 
            ? 'bg-[#0A0F0D]/95 backdrop-blur-xl shadow-lg shadow-black/20' 
            : 'bg-white/95 backdrop-blur-xl shadow-lg shadow-black/5'
          : 'bg-gradient-to-b from-black/70 via-black/40 to-transparent backdrop-blur-[2px]'
      }`}
    >
      {/* Subtle bottom border for definition */}
      <div className={`absolute bottom-0 left-0 right-0 h-px transition-opacity duration-500 ${
        isScrolled 
          ? isDark ? 'opacity-100 bg-green-900/30' : 'opacity-100 bg-gray-200'
          : 'opacity-30 bg-white/20'
      }`} />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between relative">
        {/* Logo */}
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
        >
          <img 
            src={companyInfo.logoUrl} 
            alt={companyInfo.name} 
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollToSection(link.id)}
              className={`animated-underline font-medium text-sm px-4 py-2 transition-colors duration-300 ${
                isScrolled
                  ? isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </button>
          ))}
          
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`ml-4 p-2.5 rounded-full transition-all duration-300 ${
              isScrolled
                ? isDark 
                  ? 'bg-[#1A2420] text-amber-400 hover:bg-[#243028]' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                : 'bg-white/10 text-white hover:bg-white/20 backdrop-blur-sm'
            }`}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            onClick={() => scrollToSection('contact')}
            className="ml-4 bg-gradient-to-r from-[#006633] to-[#00873D] text-white font-semibold text-sm px-6 py-2.5 rounded-full hover:shadow-lg hover:shadow-green-500/25 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
          >
            Get Started
          </button>
        </nav>

        {/* Mobile Menu Button */}
        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            className={`p-2 rounded-full transition-all duration-300 ${
              isScrolled
                ? isDark 
                  ? 'bg-[#1A2420] text-amber-400' 
                  : 'bg-gray-100 text-gray-600'
                : 'bg-white/10 text-white backdrop-blur-sm'
            }`}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button
            className={`p-2 rounded-full transition-all duration-300 ${
              isScrolled
                ? isDark ? 'text-white' : 'text-gray-800'
                : 'text-white bg-white/10 backdrop-blur-sm'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className={`md:hidden border-t ${
          isDark 
            ? 'bg-[#0A0F0D] border-green-900/30' 
            : 'bg-white border-gray-100'
        }`}>
          <nav className="flex flex-col px-6 py-6 gap-2">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`font-medium text-base text-left py-3 px-4 rounded-lg transition-colors ${
                  isDark 
                    ? 'text-gray-300 hover:text-white hover:bg-green-900/20' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {link.label}
              </button>
            ))}
            <button
              onClick={() => scrollToSection('contact')}
              className="mt-4 bg-gradient-to-r from-[#006633] to-[#00873D] text-white font-semibold text-base px-6 py-4 rounded-full hover:shadow-lg transition-all"
            >
              Get Started
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default NexmoveHeader;
